import React from 'react'

type BadgeVariant = 'priority' | 'status' | 'plan' | 'tag'

interface BadgeProps {
  variant: BadgeVariant
  // We accept anything because the backend has historically sent numbers and
  // nulls into status/plan/priority columns. Coerced to string at render time.
  value: string | number | null | undefined
  className?: string
}

const variantStyles: Record<string, string> = {
  // Priority
  'priority-low': 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  'priority-medium': 'bg-warning-light text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  'priority-high': 'bg-error-light text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',

  // Status
  'status-open': 'bg-success-light text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  'status-online': 'bg-success-light text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  'status-queued': 'bg-warning-light text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  'status-missed': 'bg-error-light text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  'status-reopened': 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  'status-closed': 'bg-surface-tertiary text-txt-secondary border-border',
  'status-auto_closed': 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  'status-archived': 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  'status-offline': 'bg-surface-tertiary text-txt-secondary border-border',
  'status-on_break': 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800',

  // Plan
  'plan-enterprise': 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  'plan-pro': 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 border-brand-200 dark:border-brand-800',
  'plan-free': 'bg-surface-tertiary text-txt-secondary border-border',
  'plan-whatsapp': 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800',
  'plan-starter': 'bg-surface-tertiary text-txt-secondary border-border',
  'plan-professional': 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 border-brand-200 dark:border-brand-800',

  // Tag default
  'tag-default': 'bg-surface-tertiary text-txt-secondary border-border',
}

export const Badge: React.FC<BadgeProps> = ({ variant, value, className = '' }) => {
  // Coerce non-string / null / undefined values to a safe string so .charAt
  // and .toLowerCase calls below can never throw. Anything non-stringy
  // (numbers, nulls, etc.) becomes '—'.
  const safeValue: string =
    typeof value === 'string' && value.length > 0
      ? value
      : value == null
        ? '—'
        : String(value)

  const key = `${variant}-${safeValue.toLowerCase()}`
  const style = variantStyles[key] || variantStyles['tag-default']

  const displayValue =
    safeValue === 'auto_closed'
      ? 'Auto Closed'
      : safeValue.charAt(0).toUpperCase() + safeValue.slice(1).replace('_', ' ')

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border leading-tight ${style} ${className}`}
    >
      {variant === 'status' && (
        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
          safeValue === 'open' || safeValue === 'online' ? 'bg-green-500' :
          safeValue === 'queued' ? 'bg-yellow-500' :
          safeValue === 'missed' ? 'bg-red-500' :
          safeValue === 'auto_closed' ? 'bg-orange-500' :
          safeValue === 'reopened' ? 'bg-purple-500' :
          safeValue === 'on_break' ? 'bg-orange-500' :
          safeValue === 'archived' ? 'bg-amber-500' : 'bg-gray-400'
        }`} />
      )}
      {displayValue}
    </span>
  )
}
